import {
  DropdownProps,
  DropdownSectionProps,
  Table as HeroTable,
  TableBodyProps as HeroTableBodyProps,
  TableColumn as HeroTableColumn,
  TableColumnProps as HeroTableColumnProps,
  TableProps as HeroTableProps,
  Selection,
  SortDescriptor,
  Spinner,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@heroui/react";
import clsx from "clsx";
import Tooltip from "ds_legacy/components/Tooltip";
import { ArrowUpDownIcon, InfoIcon, MoreVerticalIcon } from "lucide-react";
import { ReactNode, useCallback, useMemo } from "react";
import { useTranslations } from "translations";
import { Button, type ButtonProps } from "../button/button";
import { Dropdown, DropdownMenuItemProps } from "../dropdown/dropdown";
import { Pagination, PaginationProps } from "../pagination/pagination";
import { EmptyTableState } from "./empty-table-state";

export type TableProps<T extends { id: string | number }> = Omit<
  HeroTableProps,
  "children"
> & {
  actionComponent?: (item: T) => React.ReactNode;
  actionConfig?: TableActionConfig;
  actions?: TableAction<T>[];
  bottomContent?: HeroTableProps["bottomContent"];
  centeredPagination?: PaginationProps["isCentered"];
  classNameCell?: (column: TableColumn<T>, item: T) => string | undefined;
  classNameHeader?: (column: TableColumn<T>) => string | undefined;
  columns: TableColumn<T>[];
  data?: T[] | null;
  emptyContent?: HeroTableBodyProps<T>["emptyContent"];
  highlightRows?: (string | number)[];
  isLoading?: HeroTableBodyProps<T>["isLoading"];
  loadingContent?: HeroTableBodyProps<T>["loadingContent"];
  onPageChange?: (page: number) => void;
  onRowAction?: HeroTableProps["onRowAction"];
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onSelectionChange?: (selection: Selection) => void;
  onSortChange?: (descriptor: SortDescriptor) => void;
  page?: PaginationProps["page"];
  rowTestIdPrefix?: string;
  rowsPerPage?: PaginationProps["rowsPerPage"];
  rowsPerPageOptions?: PaginationProps["rowsPerPageOptions"];
  showPagination?: boolean;
  total?: PaginationProps["total"];
};

export type TableColumn<T extends object> = {
  allowsSorting?: HeroTableColumnProps<T>["allowsSorting"];
  handleInfoClick?: (e: React.MouseEvent) => void;
  info?: string | ReactNode;
  key: keyof T;
  label: string;
  render?: (item: T) => ReactNode;
};

export type TableAction<T> = {
  color?: DropdownMenuItemProps["color"];
  icon?: DropdownMenuItemProps["startContent"];
  key: DropdownMenuItemProps["key"];
  label: DropdownSectionProps["title"];
  onPress: (item: T) => void;
};

type TableActionConfig = {
  dropdownAriaLabel?: DropdownProps["aria-label"];
  triggerAriaLabel?: ButtonProps["aria-label"];
  triggerSize?: ButtonProps["size"];
  triggerVariant?: ButtonProps["variant"];
};

function DefaultLoadingState({ message = "Loading..." }: { message?: string }) {
  return (
    <div className="mt-16 flex flex-col items-center justify-center p-8">
      <Spinner color="primary" size="md" />
      <p className="mt-4 text-primary">{message}</p>
    </div>
  );
}

export function Table<T extends { id: string | number }>({
  actionComponent,
  actionConfig,
  actions,
  bottomContent,
  centeredPagination,
  classNameCell,
  classNameHeader,
  classNames,
  columns,
  data,
  emptyContent,
  isLoading,
  loadingContent,
  onPageChange,
  onRowAction,
  onRowsPerPageChange,
  onSelectionChange,
  onSortChange,
  page,
  rowsPerPage,
  rowsPerPageOptions,
  rowTestIdPrefix = "row",
  showPagination = true,
  total,
  ...props
}: TableProps<T>) {
  const translations = useTranslations();
  const allColumns = useMemo(() => {
    if (!actions && !actionComponent) return columns;

    return [
      ...columns,
      {
        key: "actions" as keyof T,
        label: "Actions",
        render: (item: T) =>
          actionComponent
            ? actionComponent(item)
            : actions && (
                <Dropdown
                  ariaLabel={
                    actionConfig?.dropdownAriaLabel ??
                    translations.accessibility.tableMenuActionAriaLabel
                  }
                  DropdownTrigger={
                    <Button
                      aria-label={
                        actionConfig?.triggerAriaLabel ??
                        translations.accessibility.tableMenuAriaLabel
                      }
                      color="primary"
                      id="row-actions"
                      isIconOnly
                      size={actionConfig?.triggerSize ?? "sm"}
                      variant={actionConfig?.triggerVariant ?? "ghost"}
                    >
                      <MoreVerticalIcon />
                    </Button>
                  }
                  sections={[
                    {
                      items: actions.map((action) => ({
                        color: action.color ?? "primary",
                        key: action.key,
                        onPress: () => action.onPress(item),
                        startContent: action.icon,
                        title: action.label,
                      })),
                    },
                  ]}
                />
              ),
      },
    ];
  }, [columns, actions, actionConfig, actionComponent]);

  const handleSortChange = useCallback(
    (descriptor: SortDescriptor) => {
      // Only reset to first page if not already on page 1
      if (page !== 1) {
        onPageChange?.(1);
      }
      onSortChange?.(descriptor);
    },
    [onPageChange, onSortChange, page],
  );

  const defaultBottomContent = useMemo(() => {
    if (!showPagination || !total) return null;

    const currentRowsPerPage = rowsPerPage ?? 10;

    return (
      <div className="sticky bottom-0 rounded-b-2xl border-gray-200 bg-white py-4">
        <Pagination
          isCentered={centeredPagination}
          page={page ?? 1}
          rowsPerPage={currentRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          showControls
          total={total}
          variant="bordered"
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </div>
    );
  }, [
    showPagination,
    total,
    rowsPerPage,
    page,
    rowsPerPageOptions,
    onPageChange,
    onRowsPerPageChange,
  ]);

  return (
    <HeroTable
      topContentPlacement="outside"
      {...props}
      bottomContent={bottomContent ?? defaultBottomContent}
      bottomContentPlacement="outside"
      classNames={{
        base: clsx("", classNames?.base),
        sortIcon: "hidden",
        table: clsx("", classNames?.table),
        tbody: clsx(
          "data-[focus-visible=true]:outline-none",
          "[&[data-focus-visible=true]>tr:first-child]:bg-gray-50 [&[data-focus-visible=true]>tr:first-child]:z-20",
          classNames?.tbody,
        ),
        td: clsx(
          "h-[66px] border-gray-200 bg-transparent px-4 py-0 tablet:border-b-0 desktop:border-b",
          "data-[selected=true]:before:bg-transparent",
          "group-data-[disabled=true]:text-gray-600 data-[selected=true]:text-gray-900",
          "data-[focus-visible=true]:outline-offset-[-3px]",
          classNames?.td,
        ),
        th: clsx(
          "bg-gray-100 px-4 py-3 text-gray-900 data-[hover=true]:text-gray-600",
          classNames?.th,
        ),
        thead:
          "[&>tr:last-child]:!mt-0 [&>tr:last-child]:!h-0 [&>tr]:!outline-none [&>tr]:focus-within:!outline-none",
        tr: clsx(
          `relative data-[disabled=true]:bg-gray-200 data-[selected=true]:bg-primary-light [&>td]:data-[disabled=true]:!text-gray-600 [&>th:first-child]:!rounded-b-none [&>th:last-child]:!rounded-b-none`,
          "data-[focus-visible=true]:z-20",
          classNames?.tr,
        ),
        wrapper: clsx(
          "shadow-none",
          { "min-h-[500px]": isLoading, "overflow-hidden!": !data?.length },
          classNames?.wrapper,
        ),
      }}
      onRowAction={onRowAction}
      onSelectionChange={onSelectionChange}
      onSortChange={handleSortChange}
    >
      <TableHeader columns={allColumns}>
        {(column: TableColumn<T>) => (
          <HeroTableColumn
            key={String(column.key)}
            allowsSorting={column.allowsSorting}
            className={clsx(
              {
                "sr-only":
                  column.key === "actions" && (actions ?? actionComponent),
              },
              classNameHeader?.(column),
            )}
            scope="col"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="mr-4 text-[14px] font-bold wrap-break-word whitespace-normal">
                  {column.label}
                </span>
              </div>
              <div className="flex gap-2">
                {column.allowsSorting && (
                  <ArrowUpDownIcon
                    aria-label={translations.general.sort}
                    className="h-4 w-4"
                  />
                )}
                {column.info && (
                  <Tooltip
                    key={String(column.key) + "-info-tooltip"}
                    placement="top"
                    title={column.info}
                  >
                    <InfoIcon
                      aria-label={column.info?.toString()}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          </HeroTableColumn>
        )}
      </TableHeader>
      <TableBody
        emptyContent={emptyContent ?? <EmptyTableState />}
        isLoading={isLoading}
        items={data ?? []}
        loadingContent={
          loadingContent ?? (
            <DefaultLoadingState
              message={`${translations.general.loading}...`}
            />
          )
        }
      >
        {(item: T) => (
          <TableRow
            key={item.id}
            className={clsx(
              {
                "bg-primary-light": props.highlightRows?.includes(item.id),
                "bg-white": !props.highlightRows?.includes(item.id),
                "cursor-pointer": !!onRowAction,
              },
              "transition-colors hover:bg-gray-50",
            )}
            data-testid={`${rowTestIdPrefix}-${item.id}`}
          >
            {allColumns.map((column: TableColumn<T>) => {
              const isHighlighted = props.highlightRows?.includes(item.id);
              const isActions = column.key === "actions";

              return (
                <TableCell
                  key={String(column.key) + item.id}
                  className={clsx(
                    {
                      "font-semibold": isHighlighted,
                      "px-0!": isActions,
                    },
                    classNameCell?.(column, item),
                  )}
                  onClick={isActions ? (e) => e.stopPropagation() : undefined}
                >
                  {column.render
                    ? column.render(item)
                    : String(item[column.key])}
                </TableCell>
              );
            })}
          </TableRow>
        )}
      </TableBody>
    </HeroTable>
  );
}

Table.displayName = "RecareUI.Table";
